/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 安全隐患反馈Modal组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-04 19:27:23
 */
import React, { useState, useCallback } from 'react'
import { Modal, Button, Input, Checkbox, App } from 'antd'
import './FeedbackModal.css'
import { useSensorTracking } from '../hooks'

interface XCFeedbackModalProps {
  visible: boolean
  onCancel?: () => void
  onSubmit?: (
    score: number,
    content: string,
    willPhoneCallback?: boolean
  ) => void
}

/**
 * 安全隐患反馈Modal组件
 * 包含评分和文本反馈功能
 */
const FeedbackModal: React.FC<XCFeedbackModalProps> = ({
  visible,
  onCancel,
  onSubmit
}) => {
  const { message } = App.useApp()
  const [rating, setRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<string>('')
  const [feedbackError, setFeedbackError] = useState<string>('')
  const [willPhoneCallback, setWillPhoneCallback] = useState<boolean>(true)

  /**
   * 处理评分点击
   */
  const handleRatingClick = useCallback((value: number) => {
    setRating(value)
  }, [])

  /**
   * 处理反馈输入框失焦
   */
  const handleFeedbackBlur = useCallback(() => {
    if (feedback.trim().length === 0) {
      setFeedbackError('请输入内容')
    } else {
      setFeedbackError('')
    }
  }, [feedback])

  /**
   * 处理反馈输入变化
   */
  const handleFeedbackChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      setFeedback(value)
      if (value.trim().length > 0) {
        setFeedbackError('')
      }
    },
    []
  )

  /**
   * 处理提交
   */
  const handleSubmit = useCallback(() => {
    // 验证评分
    if (rating === null) {
      message.error('请选择满意度评分')
      return
    }

    // 验证反馈内容
    if (feedback.trim().length === 0) {
      setFeedbackError('请输入内容')
      return
    }

    message.success('反馈提交成功，感谢您的宝贵意见！')

    // 重置表单
    setRating(null)
    setFeedback('')
    setFeedbackError('')
    setWillPhoneCallback(true)
    // 隐藏Modal
    onSubmit && onSubmit(rating, feedback.trim(), willPhoneCallback)
  }, [rating, feedback, willPhoneCallback, onSubmit])

  /**
   * 处理取消
   */
  const handleCancel = useCallback(() => {
    // 重置表单
    setRating(null)
    setFeedback('')
    setFeedbackError('')
    setWillPhoneCallback(true)
    // 隐藏Modal
    onCancel && onCancel()
  }, [onCancel])

  /**
   * 渲染评分按钮
   */
  const renderRatingButtons = () => {
    const buttons: React.ReactElement[] = []
    for (let i = 0; i <= 10; i++) {
      buttons.push(
        <button
          key={i}
          className={`rating-button ${rating === i ? 'selected' : ''}`}
          onClick={() => handleRatingClick(i)}
        >
          {i}
        </button>
      )
    }
    return buttons
  }

  return (
    <Modal
      title="AI安全隐患识别反馈"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      className="feedback-modal"
    >
      <div className="feedback-content">
        <div className="question-section">
          <div className="question-title">
            <span className="required">*</span>
            1. 您对安全识别隐患结果的满意度如何？
          </div>
          <div className="rating-labels">
            <span className="rating-label-left">非常不满意</span>
            <span className="rating-label-right">非常满意</span>
          </div>
          <div className="rating-buttons">{renderRatingButtons()}</div>
        </div>

        <div className="question-section">
          <div className="question-title">
            <span className="required">*</span>
            2. 请说明主要原因及具体改进建议：
          </div>
          <Input.TextArea
            placeholder="请输入"
            value={feedback}
            onChange={handleFeedbackChange}
            onBlur={handleFeedbackBlur}
            rows={4}
            className={feedbackError ? 'error' : ''}
          />
          {feedbackError && (
            <div className="error-message">{feedbackError}</div>
          )}
        </div>

        <div className="question-section">
          <div className="question-title">
            <Checkbox
              checked={willPhoneCallback}
              onChange={(e) => setWillPhoneCallback(e.target.checked)}
              className="phone-callback-checkbox"
            />
            愿意电话回访，成为AI建筑安全隐患排查的人类贡献者
          </div>
        </div>

        <div className="button-section">
          <Button
            type="primary"
            onClick={handleSubmit}
            className="submit-button"
          >
            提交
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default FeedbackModal
